/**
 * Content-addressed keys for the verification cache.
 *
 * Each definition is hashed together with the hashes of everything it
 * references, exactly like a git tree. A level's key mixes in the hashes of
 * every definition it transitively depends on, so editing one definition only
 * invalidates the levels that actually use it, the layering the design calls
 * for, without ever freezing old code.
 *
 * A reference always points at a definition written before it, so this walk is
 * over a graph that cannot have a cycle in it; the guard below is what keeps a
 * library that somehow did from hanging the key rather than hashing oddly.
 *
 * A level's key also covers its own cases. A report says what those cases came
 * to, so a run of the same definitions against different cases is a different
 * report: without them in the key, a cached two-case report would be replayed
 * for a level that now has one case.
 */

import type { Level } from '@spec';

const FNV_OFFSET = 0xcbf29ce484222325n;
const FNV_PRIME = 0x100000001b3n;
const MASK = 0xffffffffffffffffn;

/** 64-bit FNV-1a. Used for cache addressing, not for security. */
export function hashText(text: string): string {
  let hash = FNV_OFFSET;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= BigInt(text.charCodeAt(index));
    hash = (hash * FNV_PRIME) & MASK;
  }
  return hash.toString(16).padStart(16, '0');
}

/** Hashes every definition, mixing in the hashes of its references. */
export function definitionHashes(
  definitions: Record<string, string>,
  references: Map<string, string[]>,
): Map<string, string> {
  const hashes = new Map<string, string>();
  const visiting = new Set<string>();

  const visit = (name: string): string => {
    const cached = hashes.get(name);
    if (cached !== undefined) {
      return cached;
    }
    if (visiting.has(name)) {
      return 'cycle';
    }
    visiting.add(name);
    const children = (references.get(name) ?? [])
      .filter((child) => child in definitions)
      .map((child) => `${child}:${visit(child)}`)
      .sort();
    visiting.delete(name);
    const hash = hashText(`${name}\u0000${definitions[name] ?? ''}\u0000${children.join('\u0001')}`);
    hashes.set(name, hash);
    return hash;
  };

  for (const name of Object.keys(definitions)) {
    visit(name);
  }
  return hashes;
}

/** Every definition a level depends on, transitively. */
function levelDependencies(
  level: Level,
  mentioned: Iterable<string>,
  definitions: Record<string, string>,
  references: Map<string, string[]>,
): Set<string> {
  const dependencies = new Set<string>();
  const queue: string[] = [];

  const add = (name: string): void => {
    if (name in definitions && !dependencies.has(name)) {
      dependencies.add(name);
      queue.push(name);
    }
  };

  for (const name of mentioned) {
    add(name);
  }
  for (const slot of level.slots) {
    add(slot.name);
  }
  for (const name of level.requires ?? []) {
    add(name);
  }
  while (queue.length > 0) {
    const name = queue.pop() as string;
    for (const child of references.get(name) ?? []) {
      add(child);
    }
  }
  return dependencies;
}

/**
 * The cache key of a level: its identity, the content revision, and the hashes
 * of every definition it depends on.
 */
export function levelKey(
  level: Level,
  contentVersion: number,
  mentioned: Iterable<string>,
  definitions: Record<string, string>,
  references: Map<string, string[]>,
  hashes: Map<string, string>,
): string {
  const parts = [...levelDependencies(level, mentioned, definitions, references)]
    .map((name) => `${name}=${hashes.get(name) ?? '?'}`)
    .sort();
  return hashText(
    `v${contentVersion}\u0000${level.id}\u0000${casesFingerprint(level)}\u0000${parts.join('\u0001')}`,
  );
}

/**
 * Everything about a level's cases that a report depends on.
 *
 * The cases are what is checked and their labels are what the report says, so
 * both are in here. The fields are listed rather than the case being serialized
 * whole, so that the fingerprint follows what a case means; `mode` is
 * serialized as it is, so an option added to it cannot be forgotten here.
 */
function casesFingerprint(level: Level): string {
  return level.cases
    .map((entry) =>
      [
        entry.id,
        entry.label,
        entry.lhs,
        entry.rhs,
        (entry.also ?? []).join(','),
        JSON.stringify(entry.mode),
        entry.proofKind,
      ].join('\u0002'),
    )
    .join('\u0003');
}
